import { Component, Inject, OnInit } from '@angular/core';
import { APP_ICONS } from '../shared/icons/app-icons';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, ConfigStateService } from '@abp/ng.core';
import { OAuthService } from 'angular-oauth2-oidc';
import { MetroToastService } from '../shared/services/metro-toast.service';
import { BiometricAuthService } from '../shared/services/biometric-auth.service';
import { CommunicationService } from '../shared/services/comunication.service';
import { eAccountComponents } from '@abp/ng.account';
import { catchError, throwError, finalize, from, switchMap, tap, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';

const BIOMETRIC_SESSION_KEY = 'wp_biometric_session_active';
const BIOMETRIC_CREDENTIALS_KEY = 'wp_biometric_credentials';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html'
})
export class LoginComponent implements OnInit
{
  loginForm!: FormGroup;
  inProgress?: boolean;
  isSelfRegistrationEnabled = true;
  authWrapperKey = eAccountComponents.AuthWrapper;
  returnUrl: string = '';
  logoSrc = 'assets/images/logo/logo-landscape.png';
  iconGoogle = APP_ICONS.google;
  biometricUnlockMode = false;
  biometricError = '';
  showBiometricUnlockButton = false;

  constructor(
    private fb: FormBuilder,
    public authService: AuthService,
    private config: ConfigStateService,
    private toasterService: MetroToastService,
    @Inject(DOCUMENT) private doc: Document,
    private oauth: OAuthService,
    private router: Router,
    private biometric: BiometricAuthService,
    private communicationService: CommunicationService
    ) {

    this.returnUrl = location.href;
  }

  ngOnInit() {
    // Si el usuario ya está autenticado, redirigir al home
    if (this.authService.isAuthenticated) {
      this.router.navigate(['/']);
      return;
    }

    // Si hay credencial biométrica registrada, mostrar desbloqueo con huella
    if (this.biometric.isSupported() && this.biometric.hasCredential()) {
      this.biometricUnlockMode = true;
      this.buildForm();
      this.addSplashClickListener();
      setTimeout(() => this.unlockWithBiometric(), 300);
      return;
    }

    this.buildForm();
  }

  async unlockWithBiometric() {
    this.biometricError = '';
    try {
      const ok = await this.biometric.authenticate();
      if (!ok) {
        this.biometricError = 'No se pudo verificar la identidad';
        this.removeSplash();
        this.biometricUnlockMode = false;
        return;
      }

      // Intentar refrescar el token si hay refresh token
      let refreshToken = this.oauth.getRefreshToken();
      if (!refreshToken) {
        refreshToken = localStorage.getItem('wp_refresh_token');
        if (refreshToken) {
          (this.oauth as any)._storage.setItem('refresh_token', refreshToken);
        }
      }

      if (refreshToken) {
        try {
          await this.oauth.refreshToken();
          // Con rolling tokens, el servidor emite un refresh token NUEVO en cada uso.
          const newRefreshToken = this.oauth.getRefreshToken();
          if (newRefreshToken) {
            localStorage.setItem('wp_refresh_token', newRefreshToken);
          }
          await firstValueFrom(this.config.refreshAppState());
          this.removeSplash();
          this.communicationService.notifyBiometricLoginComplete();
          const redirectUrl = new URL(this.doc.location.href).searchParams.get('returnUrl') || '/';
          this.router.navigateByUrl(redirectUrl);
          return;
        } catch (refreshErr: any) {
          // Refresh token inválido o expirado, intentar con credenciales
        }
      }

      // Si el refresh falló, intentar login completo con credenciales guardadas
      const credentialsEncoded = localStorage.getItem(BIOMETRIC_CREDENTIALS_KEY);
      if (credentialsEncoded) {
        try {
          const credentials = JSON.parse(atob(credentialsEncoded));
          this.inProgress = true;
          await this.authService.loginUsingGrant('password', {
            username: credentials.username,
            password: credentials.password,
            scope: environment.oAuthConfig.scope,
          });
          await firstValueFrom(this.config.refreshAppState());
          this.removeSplash();
          this.communicationService.notifyBiometricLoginComplete();
          const redirectUrl = new URL(this.doc.location.href).searchParams.get('returnUrl') || '/';
          this.router.navigateByUrl(redirectUrl);
          return;
        } catch (loginErr: any) {
          // Login con credenciales falló
        } finally {
          this.inProgress = false;
        }
      }

      // No hay refresh token ni credenciales válidas → limpiar sesión biométrica
      localStorage.removeItem(BIOMETRIC_SESSION_KEY);
      localStorage.removeItem(BIOMETRIC_CREDENTIALS_KEY);
      this.removeSplash();
      this.biometricUnlockMode = false;
      this.biometricError = '';
    } catch (e: any) {
      this.removeSplash();
      this.biometricUnlockMode = false;
      this.biometricError = 'No se pudo verificar la identidad';
    }
  }

  fallbackToLogin() {
    this.biometricUnlockMode = false;
  }

  private addSplashClickListener() {
    const splashScreen = this.doc.getElementById('splash-screen');
    if (splashScreen) {
      const pointerDownHandler = () => {
        this.removeSplash();
        this.biometricUnlockMode = false;
        this.doc.removeEventListener('pointerdown', pointerDownHandler);
      };
      this.doc.addEventListener('pointerdown', pointerDownHandler);
    }
  }

  private removeSplash() {
    this.doc.body.classList.remove('splash-active');
  }


  protected buildForm() {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.maxLength(255)]],
      password: ['', [Validators.required, Validators.maxLength(128)]],
      rememberMe: [true]
    });

  }
 loginWithGoogle() {
    const api = environment.apis.default.url;          // https://localhost:44332
    const returnUrl = `${environment.application.baseUrl}/auth/google-callback`;
    const url =`${api}/external/google?returnUrl=${encodeURIComponent(returnUrl)}`;
    this.doc.location.href = url
  }

  onSubmit() {

    if (this.inProgress) return;

    this.inProgress = true;
    const { username, password, rememberMe } = this.loginForm.value;
    const redirectUrl = new URL(this.doc.location.href).searchParams.get('returnUrl') || '/';

    from(this.authService.loginUsingGrant('password', {
      username,
      password,
      scope: environment.oAuthConfig.scope,
    }))
      .pipe(
        switchMap(() => this.config.refreshAppState()),
        switchMap(async () => {
          localStorage.setItem('remember_me', JSON.stringify(rememberMe));
          // Registrar biometría ANTES de navegar (para que el componente no se destruya)
          if (this.biometric.shouldPromptRegistration()) {
            try {
              await this.biometric.register();
            } catch { /* usuario canceló */ }
          }
          // Marcar sesión biométrica activa (sobrevive a que el auto-refresh borre tokens)
          if (this.biometric.hasCredential()) {
            localStorage.setItem(BIOMETRIC_SESSION_KEY, 'true');
            const credentials = JSON.stringify({ username, password });
            localStorage.setItem(BIOMETRIC_CREDENTIALS_KEY, btoa(credentials));
          }
          const refreshToken = this.oauth.getRefreshToken();
          if (refreshToken) {
            localStorage.setItem('wp_refresh_token', refreshToken);
          }
          this.router.navigateByUrl(redirectUrl);
        }),
        catchError(err => {
          let errorMessage = 'AbpAccount::InvalidUserNameOrPassword';
          const description = err?.error?.error_description?.toString() || '';

          if (description && !description.startsWith('Invalid')) {
            if (description.toLowerCase().includes('locked out')) {
              errorMessage = '::Account:UserLockedOut';
            } else {
              errorMessage = description;
            }
          }

          this.toasterService.error(
            errorMessage,
            '',
            { life: 7000 }
          );
          return throwError(err);
        }),
        finalize(() => (this.inProgress = false))
      )
      .subscribe(() => {
      });
  }
}
